import { useCallback, useContext, useEffect } from "react";

import { useAsyncTaskContext } from "@/modern/components/async-tasks/context/AsyncTaskContext";
import type { AsyncTask } from "@/modern/components/async-tasks/types";
import { isAsyncTaskType } from "@/modern/components/async-tasks/utils";
import { ASYNC_TASK_TYPES } from "@/other/constants/OtherKeys.jsx";
import BryntumContext from "@/react/context/bryntumContext";

type BoardManagerWithRefresh = {
  getRefreshUserBoardsListing?: () => Promise<unknown>;
};

function isBoardCreationTask(task: AsyncTask): boolean {
  return (
    isAsyncTaskType(task, ASYNC_TASK_TYPES.BOARD_DUPLICATION) ||
    isAsyncTaskType(task, ASYNC_TASK_TYPES.BOARD_IMPORT) ||
    isAsyncTaskType(task, ASYNC_TASK_TYPES.DEMO_BOARD_CREATION)
  );
}

/** Shared refresh of the account board list (`boardNameStore`). */
export function useRefreshBoardList() {
  const { boardManager } = useContext(BryntumContext) as {
    boardManager?: BoardManagerWithRefresh | null;
  };

  return useCallback(
    () => boardManager?.getRefreshUserBoardsListing?.() ?? Promise.resolve(),
    [boardManager],
  );
}

/**
 * Keep `boardNameStore` fresh when a new board is created async, even if My Swivu
 * is not mounted (e.g. user stayed on the source board until the created email).
 */
export function useBoardListRefreshOnCreation() {
  const { boardManager } = useContext(BryntumContext) as {
    boardManager?: BoardManagerWithRefresh | null;
  };
  const { isReady, register } = useAsyncTaskContext();
  const refreshBoardList = useRefreshBoardList();

  useEffect(() => {
    if (!isReady || !boardManager?.getRefreshUserBoardsListing) return undefined;

    return register({
      filter: isBoardCreationTask,
      onCompleted: () => {
        void refreshBoardList();
      },
    });
  }, [boardManager, isReady, refreshBoardList, register]);
}
